import {
  getSourceLabel,
  useGetThresholdFromParams,
  useValueWithUnit
} from '@weenat/client/dist/core/alerts/alerts'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import {
  Alert,
  Predicate,
  getPredicateKey,
  isAppleScabParams,
  isBased,
  isCumul,
  isForecasted,
  isGDDTMaxed,
  isWaterStatusThresholded
} from '@weenat/client/dist/resources/alerts.types'
import { useIntl } from '@weenat/wintl'
import Button from 'app/src/kit/Button'
import Table, { TableCell, TableColumn } from 'app/src/kit/Table'
import Text from 'app/src/kit/Text'
import isEmpty from 'lodash-es/isEmpty'
import isNil from 'lodash-es/isNil'
import isNumber from 'lodash-es/isNumber'
import moment from 'moment-timezone'
import { useCallback, useMemo } from 'react'
import Icons from '../kit/Icons'
import TextTooltip from '../kit/tooltips/TextTooltip'

const SMALLER_CELL_WIDTH = 96

interface PredicateRowProps {
  isAllowingEdition?: boolean
  onEdit: () => void
  onRemove: () => void
  predicate: Predicate
}

const PredicateRow: FC<PredicateRowProps> = ({
  isAllowingEdition = true,
  onEdit,
  onRemove,
  predicate
}) => {
  const { t } = useIntl()
  const { source, op, params } = predicate

  const { convertValue } = useConvertedValue()
  const key = getPredicateKey(predicate)

  const thresholdValue = useGetThresholdFromParams(params)
  const valueWithUnit = useValueWithUnit(
    typeof thresholdValue === 'number' ? thresholdValue : undefined,
    predicate
  )

  return (
    <>
      <TableCell $ellipsis>{!isNil(source) && <>{getSourceLabel(source)}</>}</TableCell>
      <TableCell $ellipsis>
        {!isNil(op) ? (
          <>
            {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              !isEmpty(t(`alerts.predicateKeys.${key}.label`))
                ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  t(`alerts.predicateKeys.${key}.label`)
                : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  t(`alerts.operators.${op}.label`)
            }
          </>
        ) : null}
      </TableCell>
      <TableCell $ellipsis>
        {!isNil(thresholdValue) && !isNil(params) ? (
          <>
            {isAppleScabParams(params) && !isNumber(thresholdValue) ? (
              <Text $fontSize='sm'>{thresholdValue.infectionLevels}</Text>
            ) : isWaterStatusThresholded(params) && !isNumber(thresholdValue) ? (
              <Box>
                <Text $fontSize='sm'>
                  {params.aggregateByMetric
                    ? t('models.common.fields.overview_view.label')
                    : thresholdValue.horizons}
                </Text>
                <Text $fontSize='sm'>{thresholdValue.thresholds}</Text>
              </Box>
            ) : isForecasted(params) ? (
              <>
                {t(
                  'alerts.paramSetups.fieldLabels.amountInHours.optionsLabel',
                  { numberOfHours: thresholdValue },
                  { capitalize: true }
                )}
              </>
            ) : !isCumul(params) ? (
              <>{valueWithUnit}</>
            ) : (
              <Box>
                {isBased(params) ? (
                  <Text $fontSize='sm'>
                    {`${t('models.alert.fields.base.label', { capitalize: true })} ${convertValue({
                      value: params.base,
                      metric: 'T'
                    })}`}
                  </Text>
                ) : null}
                {isGDDTMaxed(params) && !isNil(params.gddTMax) && (
                  <Text $fontSize='sm'>
                    {`${t('models.alert.fields.threshold.label', { capitalize: true })} ${convertValue(
                      {
                        value: params.gddTMax,
                        metric: 'T'
                      }
                    )}`}
                  </Text>
                )}
                {!isNil(params.since) ? (
                  <Text $fontSize='sm'>{moment.unix(params.since).format('lll')}</Text>
                ) : null}

                <Text $fontSize='sm'>{valueWithUnit}</Text>
                {params.isRepeatable ? (
                  <Text $fontSize='sm'>
                    {t('alerts.paramSetups.fieldLabels.repeatable_abbrev')}
                  </Text>
                ) : null}
              </Box>
            )}
          </>
        ) : null}
      </TableCell>
      {isAllowingEdition && (
        <TableCell $width={SMALLER_CELL_WIDTH}>
          <Flex $alignItems='center' $justifyContent='flex-end'>
            <TextTooltip content={t('alerts.edit_predicate', { capitalize: true })}>
              <Icons.Edit $ml='md' $size='lg' onPress={onEdit} />
            </TextTooltip>
            <TextTooltip content={t('alerts.delete_predicate', { capitalize: true })}>
              <Icons.Bin $ml='md' $size='lg' onPress={onRemove} />
            </TextTooltip>
          </Flex>
        </TableCell>
      )}
    </>
  )
}

interface PredicateTableProps {
  isAllowingEdition?: boolean
  onPredicateAddition?: (predicates: Alert['predicates']) => void
  onPredicateDeletion?: (idx: number) => void
  onPredicateEdition?: (idx: number) => void
  predicates: Alert['predicates']
}

const PredicateTable: FC<PredicateTableProps> = ({
  isAllowingEdition = true,
  onPredicateAddition,
  onPredicateDeletion,
  onPredicateEdition,
  predicates
}) => {
  const { t } = useIntl()
  const valuesFromPredicates = useMemo(() => Object.values(predicates), [predicates])

  const columns = useMemo(() => {
    const results: TableColumn[] = []

    results.push(
      { label: t('alerts.predicate_source'), width: 1 },
      { label: t('alerts.predicate_op'), width: 1 },
      { label: t('alerts.predicate_parameters'), width: 1 }
    )

    if (isAllowingEdition) results.push({ label: '', width: '96px' })

    return results
  }, [isAllowingEdition, t])

  const renderItem = useCallback(
    (pdc: Predicate, idx: number) => {
      return (
        <PredicateRow
          key={`${getPredicateKey(pdc)}`}
          predicate={pdc}
          onRemove={() => onPredicateDeletion?.(idx)}
          onEdit={() => onPredicateEdition?.(idx)}
          isAllowingEdition={isAllowingEdition}
        />
      )
    },
    [onPredicateDeletion, onPredicateEdition, isAllowingEdition]
  )

  return (
    <Box>
      <Table
        outlined
        data={valuesFromPredicates}
        renderRow={renderItem}
        keyExtractor={getPredicateKey}
        tableName='alert-predicates'
        columns={columns}
        emptyMessage={t('alerts.no_predicates', { capitalize: true })}
        hasClickableItemInRow
      />
      {isAllowingEdition && (
        <Flex $width='100%' $p='md' $alignItems='center' $justifyContent='center'>
          <Button
            importance='sd'
            onPress={() => onPredicateAddition?.(predicates)}
            IconLeft={Icons.PlusSignCircle}
          >
            {t('alerts.add_more_predicates', { capitalize: true })}
          </Button>
        </Flex>
      )}
    </Box>
  )
}

export default PredicateTable
